import React, { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import FooterJs from '../components/FooterJs';
import { Breadcrumb, Button, Layout, Space, Spin, Tooltip, Table } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Content, Header } from 'antd/es/layout/layout';
import HeaderJs from '../components/HeaderJs';
import Nav from '../components/Nav';
import '../CSS/ChatBot.css';
import { DeleteOutlined, SendOutlined, LikeOutlined, DislikeOutlined } from '@ant-design/icons';
import backendService from '../components/BackendService';

const AgentChat = () => {
    const { instance, accounts } = useMsal();
    const [input, setInput] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [threadId, setThreadId] = useState(null);
    const [messages, setMessages] = useState(() => {
        const saved = localStorage.getItem('chatMessages');
        const initialMessages = saved ? JSON.parse(saved) : [];
        if (initialMessages.length === 0) {
            initialMessages.push({
                role: 'assistant',
                text: "Hi, I'm SCIA Agent. You can ask me questions about active Vendor contracts registered in Ariba.",
                timestamp: new Date().toISOString(),
                sender: 'Assistant'
            });
        }
        return initialMessages;
    });

    // Persist feedbacks in localStorage
    const [feedbacks, setFeedbacks] = useState(() => {
        const saved = localStorage.getItem('chatFeedbacks');
        return saved ? JSON.parse(saved) : {};
    });

    const userName = accounts[0]?.name || 'User';
    const chatEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const [activeFeedbackMsgs, setActiveFeedbackMsgs] = useState(new Set()); // Track multiple active feedback messages
    const [pendingFeedback, setPendingFeedback] = useState({}); // { [timestamp]: { sentiment, comment } }

    // Adjust textarea height to fit content, max 5 lines (approx 5*lineHeight px)
    const adjustTextareaHeight = () => {
        const ta = textareaRef.current;
        if (ta) {
            ta.style.height = 'auto'; // reset height to shrink if user deleted lines
            const maxHeight = 5 * 24; // 5 lines * 24px line-height (adjust if needed)
            ta.style.height = `${Math.min(ta.scrollHeight, maxHeight)}px`;
        }
    };

    useEffect(() => {
        adjustTextareaHeight();
    }, [input]);

    useEffect(() => {
        localStorage.setItem('chatMessages', JSON.stringify(messages));
        if (scrollContainerRef.current) {
            const el = scrollContainerRef.current;
            el.scrollTop = el.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        if (accounts.length > 0) {
            instance.setActiveAccount(accounts[0]);
        }
    }, [accounts, instance]);

    useEffect(() => {
        localStorage.setItem('chatMessages', JSON.stringify(messages));
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        localStorage.setItem('chatFeedbacks', JSON.stringify(feedbacks));
    }, [feedbacks]);

    const getRedirectUri = () => {
        if (typeof window !== 'undefined') {
            return window.location.origin;
        }
        return '';
    };

    const sendMessage = async () => {
        const messageContent = input.trim();
        if (!messageContent) return;

        const userMessage = {
            role: 'user',
            text: messageContent,
            timestamp: new Date().toISOString(),
            sender: userName
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);
        setError(null);

        try {
            let tokenResponse;
            try {
                tokenResponse = instance.acquireTokenSilent({
                    scopes: ['api://df8ffbf9-d878-4ccc-b0cd-6d97ac095b90/SCIA.Read'],
                    account: accounts[0],
                });
            } catch (silentError) {
                console.warn('Silent token acquisition failed:', silentError);
                tokenResponse = instance.acquireTokenPopup({
                    scopes: ['api://df8ffbf9-d878-4ccc-b0cd-6d97ac095b90/SCIA.Read'],
                    account: accounts[0],
                });
            }

            const userToken = tokenResponse.accessToken;
            console.log('Acquired token:', userToken ? 'Token present' : 'No token');

            const formData = new FormData();
            formData.append('input', messageContent);

            const response = await fetch(`${getRedirectUri()}/api/chat`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Authorization': `Bearer ${userToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            const newMessages = data.map(msg => ({
                role: 'assistant',
                text: msg.text,
                timestamp: new Date().toISOString(),
                sender: 'Assistant'
            }));

            setMessages(prev => [...prev, ...newMessages]);
            setThreadId(data.threadId);
        } catch (err) {
            console.error('Error sending message:', err);
            setError('Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const clearHistory = () => {
        localStorage.removeItem('chatMessages');
        localStorage.removeItem('chatFeedbacks');
        setMessages([{
            role: 'assistant',
            text: "Hi, I'm SCIA Agent. You can ask me questions about active Vendor contracts registered in Ariba.",
            timestamp: new Date().toISOString(),
            sender: 'Assistant'
        }]);
        setFeedbacks({});
        setActiveFeedbackMsgs(new Set());
        setPendingFeedback({});
        setError(null);
    };

    const handleFeedback = async (timestamp, type, comment = '', fullText) => {
        const feedbackPayload = {
            userId: accounts[0]?.username || 'Unknown',
            threadId,
            history: messages,
            message: {
                role: 'assistant',
                text: fullText, // Use the full rendered (combined) text
                timestamp,
                sender: 'Assistant'
            },
            feedbackDetails: {
                sentiment: type,
                comment
            }
        };
        try {
            await fetch(`${getRedirectUri()}/api/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(feedbackPayload)
            });
            setFeedbacks(prev => ({
                ...prev,
                [timestamp]: { sentiment: type, comment }
            }));
            setActiveFeedbackMsgs(prev => {
                const updated = new Set(prev);
                updated.delete(timestamp);
                return updated;
            });
            setPendingFeedback(prev => {
                const updated = { ...prev };
                delete updated[timestamp];
                return updated;
            });
        } catch (err) {
            console.error('Feedback logging failed', err);
        }
    };

    const renderMessages = () => {
        const combinedMessages = [];
        let resultBuffer = [];
        let firstTimestamp = null;

        messages.forEach((msg) => {
            if (msg.role === 'assistant' && msg.text !== "Hi, I'm SCIA Agent. You can ask me questions about active Vendor contracts registered in Ariba.") {
                if (resultBuffer.length === 0) {
                    firstTimestamp = msg.timestamp; // Track first timestamp in buffer
                }
                resultBuffer.push(msg.text);
            } else {
                if (resultBuffer.length > 0) {
                    combinedMessages.push({
                        role: 'assistant',
                        text: resultBuffer.join('\n\n'),
                        timestamp: firstTimestamp, // Use first assistant timestamp
                        sender: 'Assistant'
                    });
                    resultBuffer = [];
                    firstTimestamp = null;
                }
                combinedMessages.push({
                    role: msg.role,
                    text: msg.text,
                    timestamp: msg.timestamp,
                    sender: msg.sender
                });
            }
        });

        if (resultBuffer.length > 0) {
            combinedMessages.push({
                role: 'assistant',
                text: resultBuffer.join('\n\n'),
                timestamp: firstTimestamp || new Date().toISOString(), // Fallback if needed
                sender: 'Assistant'
            });
        }

        return combinedMessages.map((msg, idx) => {
            const isTabulation = msg.role === 'assistant' && msg.text.startsWith('Here is the count of contracts');
            const isBotResponse = msg.role === 'assistant' && msg.text !== "Hi, I'm SCIA Agent. You can ask me questions about active Vendor contracts registered in Ariba.";

            const messageStyle = {
                padding: '10px',
                marginBottom: '10px',
                borderRadius: '8px',
                background: msg.role === 'user' ? '#e6f7ff' : '#f6f6f6',
                marginLeft: msg.role === 'user' ? '10px' : '200px',
                marginRight: msg.role === 'user' ? '200px' : '10px',
                textAlign: 'left',
                wordWrap: 'break-word',
                fontSize: '0.9em',
            };

            const feedbackSection = isBotResponse ? (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {feedbacks[msg.timestamp] ? (
                        <span>
                            {feedbacks[msg.timestamp].sentiment === 'up' ? (
                                <LikeOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                            ) : (
                                <DislikeOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
                            )}
                        </span>
                    ) : (
                        <>
                            <Button
                                icon={<LikeOutlined />}
                                type={pendingFeedback[msg.timestamp]?.sentiment === 'up' ? 'primary' : 'default'}
                                onClick={() => {
                                    setActiveFeedbackMsgs(prev => new Set(prev).add(msg.timestamp));
                                    setPendingFeedback(prev => ({
                                        ...prev,
                                        [msg.timestamp]: { sentiment: 'up', comment: '' }
                                    }));
                                }}
                            />
                            <Button
                                icon={<DislikeOutlined />}
                                type={pendingFeedback[msg.timestamp]?.sentiment === 'down' ? 'primary' : 'default'}
                                onClick={() => {
                                    setActiveFeedbackMsgs(prev => new Set(prev).add(msg.timestamp));
                                    setPendingFeedback(prev => ({
                                        ...prev,
                                        [msg.timestamp]: { sentiment: 'down', comment: '' }
                                    }));
                                }}
                            />
                            {activeFeedbackMsgs.has(msg.timestamp) && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                        type="text"
                                        value={pendingFeedback[msg.timestamp]?.comment || ''}
                                        onChange={e => setPendingFeedback(prev => ({
                                            ...prev,
                                            [msg.timestamp]: {
                                                sentiment: prev[msg.timestamp]?.sentiment || 'up',
                                                comment: e.target.value
                                            }
                                        }))}
                                        placeholder="Add a comment (optional)"
                                        style={{ width: 220, padding: '4px', borderRadius: '4px', border: '1px solid #d9d9d9' }}
                                    />
                                    <Button
                                        type="primary"
                                        onClick={() => handleFeedback(
                                            msg.timestamp,
                                            pendingFeedback[msg.timestamp].sentiment,
                                            pendingFeedback[msg.timestamp].comment,
                                            msg.text // Pass full rendered text
                                        )}
                                    >
                                        Submit Feedback
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            ) : null;

            if (isTabulation) {
                const lines = msg.text.split('\n').filter(line => line.trim());
                const tableData = [];
                let headers = [];

                lines.forEach(line => {
                    if (line.startsWith('|') && line.includes('|') && !line.toLowerCase().includes('source') && !line.startsWith('--')) {
                        const parts = line.split('|').map(p => p.trim()).filter(p => p);
                        if (parts.length > 1) {
                            if (!headers.length && parts.length >= 2) {
                                headers = parts.slice(1, -1);
                            } else if (headers.length) {
                                const rowData = {};
                                parts.slice(1, -1).forEach((value, i) => {
                                    rowData[headers[i].toLowerCase().replace(' ', '_')] = value;
                                });
                                if (parts.length === headers.length + 2) {
                                    tableData.push(rowData);
                                }
                            }
                        }
                    }
                });

                const columns = headers.map((header, index) => ({
                    title: header,
                    dataIndex: header.toLowerCase().replace(' ', '_'),
                    key: `${header.toLowerCase().replace(' ', '_')}_${index}`,
                }));

                return (
                    <div key={idx} style={messageStyle}>
                        <Space>
                            <strong>{msg.sender}</strong>
                            <span style={{ fontSize: '0.8em', color: '#666' }}>
                                {new Date(msg.timestamp).toLocaleString()}
                            </span>
                        </Space>
                        <div className="message-content">
                            <p>{lines[0]}</p>
                            {tableData.length > 0 && (
                                <Table
                                    dataSource={tableData}
                                    columns={columns}
                                    pagination={false}
                                    bordered
                                    size="small"
                                    className="ant-table-bordered"
                                />
                            )}
                        </div>
                        {feedbackSection}
                    </div>
                );
            }

            return (
                <div key={idx} style={messageStyle}>
                    <Space>
                        <strong>{msg.sender}</strong>
                        <span style={{ fontSize: '0.8em', color: '#666' }}>
                            {new Date(msg.timestamp).toLocaleString()}
                        </span>
                    </Space>
                    <div className="message-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.text}
                        </ReactMarkdown>
                    </div>
                    {feedbackSection}
                </div>
            );
        });
    };

    return (
        <div className="SCAUX">
            <Layout style={{ minHeight: '100vh', overflowX: 'hidden', overflowY: 'hidden' }}>
                <Header className="header">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0, shrink-to-fit=no" />
                    <HeaderJs />
                </Header>
                <Layout hasSider>
                    <Nav />
                    <Content>
                        <div className="content">
                            <div className="scrollable-content">
                                <div className="searchfilter">
                                    <Breadcrumb className="page-title">
                                        <Breadcrumb.Item>
                                            <span className="page-title-label">Supplier Contracts Copilot</span>
                                        </Breadcrumb.Item>
                                    </Breadcrumb>
                                </div>
                                <div id="chat-container">
                                    {error && <p style={{ color: 'red' }}>{error}</p>}
                                    <div className="message-container" ref={scrollContainerRef}>
                                        {renderMessages()}
                                        {loading && <Spin tip="Processing..." />}
                                    </div>
                                    <div className="chat-input-container">
                                        <textarea
                                            ref={textareaRef}
                                            value={input}
                                            onChange={(e) => setInput(e.target.value)}
                                            placeholder="Type your message"
                                            style={{
                                                padding: '8px',
                                                borderRadius: '4px',
                                                border: '1px solid #d9d9d9',
                                                flex: 1,
                                                resize: 'none',
                                                overflowY: 'auto',
                                                lineHeight: '24px',
                                                maxHeight: '120px',
                                                fontSize: '1em',
                                                fontFamily: 'inherit',
                                                boxSizing: 'border-box',
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    if (!loading && input.trim()) {
                                                        sendMessage();
                                                    }
                                                }
                                            }}
                                            disabled={loading}
                                        />
                                        <Tooltip title="Send">
                                            <Button
                                                type="primary"
                                                icon={<SendOutlined />}
                                                onClick={sendMessage}
                                            />
                                        </Tooltip>
                                        <Button
                                            danger
                                            onClick={clearHistory}
                                        >
                                            Clear History
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Content>
                </Layout>
                <FooterJs />
            </Layout>
        </div>
    );
};

export default AgentChat;